import {
    GetFulfillmentSummaryRequestSchema,
    GetFulfillmentSummaryResponseSchema,
    GetOrderResponseSchema,
    PostDirectFulfillmentResponseSchema,
    PostFulfillmentSummaryByPositionRequestSchema,
    PostFulfillmentSummaryByPositionResponseSchema,
    PostOrderFulfillRequestSchema,
} from '@schuettflix/interfaces';
import { PaymentTermsSettings } from '@/clients/finance/types/FinanceSettings';
import { z } from 'zod';
import { useHttpClientContext } from '@/shared/context/HttpClientContext.tsx';
import { GetOrderOptions } from '@/clients/order/useOrder.ts';
import {
    PostOrderConfigurationRequest,
    PostOrderConfigurationResponseSchema,
} from '@/shared/types/orderV2/api/post-order-configuration.ts';

export const useGetOrderById = () => {
    const { orderClient } = useHttpClientContext();
    return {
        getOrderById: (orderId: string, params?: Omit<GetOrderOptions, 'fetchOnce'>) =>
            orderClient
                .get<z.input<typeof GetOrderResponseSchema>>(`/v1/order/${orderId}`, {
                    params,
                })
                .then(res => GetOrderResponseSchema.parse(res.data)),
    };
};

export const useSubmitOrderApiV2 = () => {
    const { orderClientV2 } = useHttpClientContext();

    return {
        submitOrderV2: (body: PostOrderConfigurationRequest, idempotencyKey?: string) =>
            orderClientV2
                .post('/v2/orders', body, {
                    ...(idempotencyKey && { headers: { 'idempotency-key': idempotencyKey } }),
                })
                .then(res => PostOrderConfigurationResponseSchema.parse(res.data)),
    };
};

export type GetFulfillmentSummaryPayload = z.input<typeof GetFulfillmentSummaryRequestSchema>;
export const useGetFulfillmentSummaryById = () => {
    const { orderClient } = useHttpClientContext();

    return {
        getFulfillmentSummaryById: async (orderId: string, payload: GetFulfillmentSummaryPayload) => {
            return await orderClient.post(`/v1/order/${orderId}/fulfillment-summary`, payload).then(res => {
                return GetFulfillmentSummaryResponseSchema.parse(res.data);
            });
        },
    };
};

export const useGetDirectFulfillmentSummaryById = () => {
    const { orderClient } = useHttpClientContext();

    return {
        getDirectFulfillmentSummary: async (
            positionId: string,
            payload: z.input<typeof PostFulfillmentSummaryByPositionRequestSchema>
        ) => {
            return await orderClient.post(`/v1/order/position/${positionId}/fulfillment-summary`, payload).then(res => {
                return PostFulfillmentSummaryByPositionResponseSchema.parse(res.data);
            });
        },
    };
};

export const useGetPaymentTerms = () => {
    const { monolithClient } = useHttpClientContext();
    return {
        getPaymentTerms: () =>
            monolithClient.get<PaymentTermsSettings>(`/finance/v1/platform/payment-terms`).then(res => res.data),
    };
};

export const useFulfillOrder = () => {
    const { customRequestClient } = useHttpClientContext();
    return {
        fulfillOrder: (orderId: string, payload: z.input<typeof PostOrderFulfillRequestSchema>) => {
            return customRequestClient.post(`/v1/order-fulfill/${orderId}`, payload);
        },
    };
};

export const useCancelOrder = () => {
    const { customRequestClient } = useHttpClientContext();
    return {
        cancelOrder: (orderId: string) => customRequestClient.delete(`/v1/order-fulfill/${orderId}`),
    };
};

export const useCreateOrderForDirectFulfillment = () => {
    const { orderClient } = useHttpClientContext();
    return {
        createOrder: (positionId: string) =>
            orderClient
                .post(`/v1/order/position/${positionId}/direct-fulfillment`)
                .then(response => PostDirectFulfillmentResponseSchema.parse(response.data)),
    };
};
